import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteGmailFilters } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../../utils/roles/client-roles';
import { FilterModel } from '../../../../../../types';
import { Messages } from '../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteFiltersDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedRows: string[];
  changeLogRoles: string[];
}

export default function DeleteFiltersDialog({
  open,
  handleClose,
  userEmail,
  selectedRows,
  changeLogRoles,
}: DeleteFiltersDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteGmailFilters(userEmail, selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          'Filter' + (selectedRows.length > 1 ? 's' : '')
        ),
      });

      queryClient.setQueryData(
        [UserKeys.GmailFilters, userEmail],
        (oldData: FilterModel[]) =>
          oldData.filter((filter) => {
            return !selectedRows.includes(filter.id!);
          })
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected filter' + (selectedRows.length > 1 ? 's' : '')
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_gmail_filter,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.filters,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.DeleteGmailFilters.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Filters"
      dialogContent={
        <Box>
          <Typography>
            <Typography>
              Do you wish to delete the{' '}
              {selectedRows.length === 1
                ? 'selected filter'
                : selectedRows.length + ' selected filters'}{' '}
              from{' '}
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                {userEmail}
              </Typography>
              's gmail filters?
            </Typography>
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
